import { useState, useEffect, useCallback } from "react";
import { useNotification } from "../context/NotificationContext";
import cgiLogo from "../assets/images/CGI-Logo.svg";
import aisLogo from "../assets/images/AIS-Logo.svg";
import { useConfirm } from "../providers/ConfirmProvider";

export interface Logo {
  id: string;
  name: string;
  logoUrl: string;
  isDefault: boolean;
  isDeletable: boolean;
  source: "asset" | "storage";
}

const DEFAULT_LOGOS: Logo[] = [
  {
    id: "cgi",
    name: "CGI",
    logoUrl: cgiLogo,
    isDefault: true,
    isDeletable: false,
    source: "asset",
  },
  {
    id: "ais",
    name: "AIS",
    logoUrl: aisLogo,
    isDefault: true,
    isDeletable: false,
    source: "asset",
  },
];

const validateLogoFile = (file: File): string | null => {
  if (file.size > 2 * 1024 * 1024) {
    return "File size must be less than 2MB";
  }

  if (!file.type.match("image/(png|jpeg|jpg)")) {
    return "Please select a PNG or JPG file";
  }

  return null;
};

const validateLogoName = (
  name: string,
  existingLogos: Logo[],
  excludeId?: string
): string | null => {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return "Please enter a name for the logo.";
  }

  const nameExists = existingLogos.some(
    (logo) =>
      logo.name.toLowerCase() === trimmedName.toLowerCase() &&
      logo.id !== excludeId
  );

  if (nameExists) {
    return `The name "${trimmedName}" is already in use.`;
  }

  return null;
};

interface UseLogosReturn {
  allLogos: Logo[];
  fetchLogos: () => Promise<void>;
  deleteLogo: (logoId: string, logoName: string) => Promise<boolean>;
  uploadLogo: (file: File, name: string) => Promise<boolean>;
  validateLogoFile: (file: File) => string | null;
  validateLogoName: (name: string, excludeId?: string) => string | null;
}

export const useLogos = (userId?: string): UseLogosReturn => {
  const [allLogos, setAllLogos] = useState<Logo[]>(DEFAULT_LOGOS);
  const { setNotification } = useNotification();
  const confirm = useConfirm();

  const fetchLogos = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user-logos`,
        {
          headers: {
            "client-principal-user-id": userId,
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        const userLogos = await response.json();
        setAllLogos([...DEFAULT_LOGOS, ...userLogos]);
      } else {
        setNotification({
          type: "error",
          message: "Failed to load user logos.",
        });
      }
    } catch (error) {
      setNotification({
        type: "error",
        message: "An error occurred while loading logos.",
      });
    }
  }, [userId, setNotification]);

  const deleteLogo = useCallback(
    async (logoId: string, logoName: string): Promise<boolean> => {
      if (!userId) {
        setNotification({
          type: "error",
          message:
            "Login info is missing. Please refresh the page or login again.",
        });
        return false;
      }

      const confirmed = await confirm({
        title: "Delete Logo",
        message: `Are you sure you want to delete "${logoName}"?`,
        confirmLabel: "Delete",
        cancelLabel: "Cancel",
      });

      if (!confirmed) return false;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/user-logos/${logoId}`,
          {
            method: "DELETE",
            headers: {
              "client-principal-user-id": userId,
            },
            credentials: "include",
          }
        );

        if (response.ok) {
          setAllLogos((prev) => prev.filter((logo) => logo.id !== logoId));
          setNotification({
            type: "success",
            message: "Logo deleted successfully!",
          });
          return true;
        } else {
          setNotification({
            type: "error",
            message: "Failed to delete logo.",
          });
          return false;
        }
      } catch (error) {
        setNotification({
          type: "error",
          message: "An error occurred while deleting the logo.",
        });
        return false;
      }
    },
    [userId, setNotification, confirm]
  );

  const uploadLogo = useCallback(
    async (file: File, name: string): Promise<boolean> => {
      if (!userId) {
        setNotification({
          type: "error",
          message:
            "Login info is missing. Please refresh the page or login again.",
        });
        return false;
      }

      const fileError = validateLogoFile(file);
      if (fileError) {
        setNotification({
          type: "error",
          message: fileError,
        });
        return false;
      }

      const nameError = validateLogoName(name, allLogos);
      if (nameError) {
        setNotification({
          type: "error",
          message: nameError,
        });
        return false;
      }

      try {
        const formData = new FormData();
        formData.append("logo", file);
        formData.append("userId", userId);
        formData.append("logoName", name.trim());

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/save-user-logo`,
          {
            method: "POST",
            body: formData,
            credentials: "include",
            headers: {
              "client-principal-user-id": userId,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();

          const newLogo: Logo = {
            id: result.logoId,
            name: name.trim(),
            logoUrl: result.logoUrl,
            isDefault: false,
            isDeletable: true,
            source: "storage",
          };

          setAllLogos((prev) => [...prev, newLogo]);
          setNotification({
            type: "success",
            message: "Logo uploaded successfully!",
          });
          return true;
        } else {
          const errorData = await response.json();
          setNotification({
            type: "error",
            message: errorData.message || "Failed to upload logo.",
          });
          return false;
        }
      } catch (error) {
        setNotification({
          type: "error",
          message: "An error occurred while uploading the logo.",
        });
        return false;
      }
    },
    [userId, setNotification, allLogos]
  );

  const validateLogoFileWrapper = useCallback((file: File): string | null => {
    return validateLogoFile(file);
  }, []);

  const validateLogoNameWrapper = useCallback(
    (name: string, excludeId?: string): string | null => {
      return validateLogoName(name, allLogos, excludeId);
    },
    [allLogos]
  );

  useEffect(() => {
    fetchLogos();
  }, [fetchLogos]);

  return {
    allLogos,
    fetchLogos,
    deleteLogo,
    uploadLogo,
    validateLogoFile: validateLogoFileWrapper,
    validateLogoName: validateLogoNameWrapper,
  };
};
